import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  LocalShipping as PickingIcon,
} from "@mui/icons-material";
import {
  useTSQInvStatusList,
  useTSMDeactivateInvStatus,
  useTSMReactivateInvStatus,
} from "../../hooks/useTSInvStatus";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { InvStatusFull } from "../../types/invStatusTypes";
import { paginationDefaults } from "../../config/settingsDefaults";

export const PageInvStatusList: React.FC = () => {
  const navigate = useNavigate();
  // const [statuses, setStatuses] = useState<InvStatusList[]>([]);
  // const [totalCount, setTotalCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "inactive"
  >("active");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [searchFilters, setSearchFilters] = useState<ListRequestFilter[][]>([
    [],
  ]);
  const [orderBy, setOrderBy] = useState<string>("name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const {
    data: statusResponse,
    isLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQInvStatusList({
    skip: pagination.page * pagination.size,
    limit: pagination.size,
    active_status: statusFilter,
    filters: searchFilters,
  });

  const [statuses, totalCount] = useMemo(
    () =>
      statusResponse?.items
        ? [statusResponse.items, statusResponse.items.length]
        : [[], 0],
    [statusResponse],
  );

  const deactivateStatusMutation = useTSMDeactivateInvStatus();
  const reactivateStatusMutation = useTSMReactivateInvStatus();

  // useEffect(() => {
  //   if (statusResponse?.items) {
  //     setStatuses(statusResponse.items);
  //   }
  // }, [statusResponse]);

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this Status?")) {
      return;
    }

    try {
      await deactivateStatusMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate Status:", err);
      setError(err.response?.data?.detail || "Failed to deactivate Status");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateStatusMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate Status:", err);
      setError(err.response?.data?.detail || "Failed to reactivate Status");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getSortValue = (status: InvStatusFull, property: string): any => {
    switch (property) {
      case "name":
        return status.name?.toLowerCase() || "";
      case "exclued_from_mrp":
        return status.exclude_from_mrp ? "yes" : "no";
      case "is_default_receive_status":
        return status.is_default_receive_status ? "yes" : "no";
      case "allow_allocation":
        return status.allow_allocation ? "yes" : "no";
      case "allow_picking":
        return status.allow_picking ? "yes" : "no";
      case "created_at":
        return new Date(status.created_at || "");
      default:
        return "";
    }
  };

  // Note: Inventory statuses are configuration data and typically don't support delete/restore operations
  // They are managed through the backend configuration and should remain stable for operational integrity

  const filteredAndSortedStatuses = useMemo(() => {
    const filtered = statuses.filter((status) => {
      const matchesSearch =
        searchTerm === "" ||
        status.name.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesSearch;
    });

    if (orderBy) {
      const sorted = [...filtered].sort((a, b) => {
        const aValue = getSortValue(a, orderBy);
        const bValue = getSortValue(b, orderBy);

        if (aValue === bValue) return 0;

        if (order === "asc") {
          return aValue < bValue ? -1 : 1;
        } else {
          return aValue > bValue ? -1 : 1;
        }
      });
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return sorted.slice(start, end);
    } else {
      const start = pagination.size * pagination.page;
      const end = start + pagination.size;
      return filtered.slice(start, end);
    }
  }, [statuses, searchTerm, orderBy, order, pagination]);

  if (isLoading && statuses.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          Inventory Status Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/inventory/status/form?mode=create")}
        >
          Create Status
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {isPlaceholderData && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <TextField
          fullWidth
          placeholder="Search statuses by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === "name" ? order : false}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={orderBy === "name" ? order : "asc"}
                  onClick={() => handleSort("name")}
                >
                  Status Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "exclude_from_mrp" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "exclude_from_mrp"}
                  direction={orderBy === "exclude_from_mrp" ? order : "asc"}
                  onClick={() => handleSort("exclude_from_mrp")}
                >
                  Exclude From MRP?
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "is_default_receive_status" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "is_default_receive_status"}
                  direction={
                    orderBy === "is_default_receive_status" ? order : "asc"
                  }
                  onClick={() => handleSort("is_default_receive_status")}
                >
                  Default Receive Status?
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "allow_allocation" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "allow_allocation"}
                  direction={orderBy === "allow_allocation" ? order : "asc"}
                  onClick={() => handleSort("allow_allocation")}
                >
                  Allow Allocation
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "allow_picking" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "allow_picking"}
                  direction={orderBy === "allow_picking" ? order : "asc"}
                  onClick={() => handleSort("allow_picking")}
                >
                  Allow Picking
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "created_at" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "created_at"}
                  direction={orderBy === "created_at" ? order : "asc"}
                  onClick={() => handleSort("created_at")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && statuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : filteredAndSortedStatuses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {searchTerm
                      ? "No statuses found matching your search."
                      : "No inventory statuses found. Create your first status to get started."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedStatuses.map((status) => (
                <TableRow key={status.id} hover>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{ ml: 1, fontWeight: "medium" }}
                    >
                      {status.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {!status.exclude_from_mrp ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {status.exclude_from_mrp ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {status.is_default_receive_status ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {status.is_default_receive_status ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {status.allow_allocation ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {status.allow_allocation ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {status.allow_picking ? (
                        <PickingIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {status.allow_picking ? "Yes" : "No"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {status.created_at &&
                      new Date(status.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/status/form?mode=view&id=${status.id}`,
                            )
                          }
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Status">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/inventory/status/form?mode=edit&id=${status.id}`,
                            )
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {!status.is_record_active ? (
                        <Tooltip title="Reactivate Status">
                          <IconButton
                            size="small"
                            onClick={() => handleReactivate(status.id)}
                            color="success"
                            disabled={reactivateStatusMutation.isPending}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title="Deactivate Status">
                          <IconButton
                            size="small"
                            onClick={() => handleDeactivate(status.id)}
                            color="error"
                            disabled={deactivateStatusMutation.isPending}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
